import React, {
    useEffect,
    useRef,
    useState
} from "react";

import {
    ArrowLeftOutlined,
    CompressOutlined,
    ExpandOutlined,
    ReloadOutlined
} from "@ant-design/icons";

import {
    useNavigate
} from "react-router-dom";

import {
    renderAsync
} from "docx-preview";


const DocxView = ({
    title,
    url
}) => {

    const navigate =
        useNavigate();

    const contentRef =
        useRef(null);

    const wrapperRef =
        useRef(null);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [reloadKey, setReloadKey] =
        useState(0);

    const [fullscreen, setFullscreen] =
        useState(false);


    useEffect(() => {

        const onFullscreen =
            () =>
                setFullscreen(
                    Boolean(
                        document
                            .fullscreenElement
                    )
                );

        document.addEventListener(
            "fullscreenchange",
            onFullscreen
        );

        return () =>
            document.removeEventListener(
                "fullscreenchange",
                onFullscreen
            );

    }, []);


    useEffect(() => {

        if (
            !url ||
            !contentRef.current
        ) {
            return;
        }

        let active =
            true;

        const load =
            async () => {

                try {

                    setLoading(true);
                    setError("");

                    const response =
                        await fetch(
                            url,
                            {
                                credentials:
                                    "include"
                            }
                        );

                    if (
                        !response.ok
                    ) {
                        throw new Error(
                            `HTTP ${response.status}`
                        );
                    }

                    const buffer =
                        await response.arrayBuffer();

                    if (!active) {
                        return;
                    }

                    contentRef
                        .current
                        .innerHTML =
                        "";

                    await renderAsync(
                        buffer,
                        contentRef.current,
                        null,
                        {
                            className:
                                "docx",

                            inWrapper:
                                true,

                            ignoreWidth:
                                false,

                            ignoreHeight:
                                false,

                            ignoreFonts:
                                false,

                            breakPages:
                                true,

                            useBase64URL:
                                true
                        }
                    );

                    if (
                        active
                    ) {
                        setLoading(
                            false
                        );
                    }

                } catch (err) {

                    console.error(
                        "DOCX LOAD ERROR:",
                        err
                    );

                    if (
                        active
                    ) {
                        setLoading(false);

                        setError(
                            err?.message ||
                            "Không mở được tài liệu DOCX."
                        );
                    }
                }
            };

        load();

        return () => {
            active =
                false;
        };

    }, [
        url,
        reloadKey
    ]);


    const toggleFullscreen =
        async () => {

            if (
                document
                    .fullscreenElement
            ) {
                await document
                    .exitFullscreen();

                return;
            }

            if (
                wrapperRef.current
                    ?.requestFullscreen
            ) {
                await wrapperRef
                    .current
                    .requestFullscreen();
            }
        };


    return (

        <div
            ref={wrapperRef}
            className="
                fixed
                inset-0
                z-[99999]
                flex
                flex-col
                bg-[#202124]
            "
            onContextMenu={
                event =>
                    event.preventDefault()
            }
        >

            <header
                className="
                    h-[56px]
                    flex
                    items-center
                    gap-2
                    px-3
                    md:px-4
                    bg-[#17191c]
                    border-b
                    border-white/10
                    text-white
                    flex-none
                "
            >

                <button
                    type="button"
                    onClick={() =>
                        navigate(-1)
                    }
                    className="
                        px-3
                        h-9
                        rounded-lg
                        flex
                        items-center
                        gap-2
                        hover:bg-white/10
                    "
                >
                    <ArrowLeftOutlined />

                    <span
                        className="
                            hidden
                            sm:inline
                            font-semibold
                            text-sm
                        "
                    >
                        THƯ VIỆN
                    </span>
                </button>


                <div
                    className="
                        flex-1
                        truncate
                        font-semibold
                        text-sm
                        md:text-[15px]
                    "
                >
                    {title ||
                        "Tài liệu DOCX"}
                </div>


                <button
                    type="button"
                    onClick={() =>
                        setReloadKey(
                            value =>
                                value + 1
                        )
                    }
                    className="
                        w-9
                        h-9
                        rounded-lg
                        hover:bg-white/10
                    "
                >
                    <ReloadOutlined />
                </button>


                <button
                    type="button"
                    onClick={
                        toggleFullscreen
                    }
                    className="
                        w-9
                        h-9
                        rounded-lg
                        hover:bg-white/10
                    "
                >
                    {fullscreen
                        ? <CompressOutlined />
                        : <ExpandOutlined />
                    }
                </button>

            </header>


            <main
                className="
                    relative
                    flex-1
                    min-h-0
                    overflow-auto
                    bg-[#2b2b2b]
                    py-5
                    px-2
                    md:px-5
                "
            >

                {loading && (

                    <div
                        className="
                            fixed
                            inset-0
                            top-[56px]
                            z-20
                            flex
                            items-center
                            justify-center
                            bg-[#202124]
                            text-gray-300
                        "
                    >
                        Đang mở tài liệu...
                    </div>
                )}


                {error && (

                    <div
                        className="
                            max-w-[520px]
                            mx-auto
                            mt-20
                            bg-white
                            rounded-xl
                            p-6
                        "
                    >
                        <div
                            className="
                                text-lg
                                font-semibold
                            "
                        >
                            Lỗi
                        </div>

                        <div
                            className="
                                mt-3
                                text-gray-600
                            "
                        >
                            Không mở được tài liệu DOCX.
                        </div>

                        <div
                            className="
                                mt-1
                                text-xs
                                text-gray-400
                            "
                        >
                            {error}
                        </div>
                    </div>
                )}


                <div
                    ref={contentRef}
                    className="
                        docx-reader
                        mx-auto
                    "
                />

            </main>


            <style>
                {`
                .docx-reader .docx-wrapper {
                    background: transparent !important;
                    padding: 0 !important;
                }

                .docx-reader .docx-wrapper > section.docx {
                    margin: 0 auto 18px !important;
                    box-shadow:
                        0 8px 30px rgba(0,0,0,.28);
                }

                .docx-reader a {
                    pointer-events: none !important;
                    text-decoration: none !important;
                    color: inherit !important;
                }
                `}
            </style>

        </div>
    );
};


export default DocxView;
